import { LocalDate } from '../../common/domain/date/local-date';
import * as moment from 'moment-timezone';

type DateRangeBias = typeof DateRange.BIAS_BEFORE | typeof DateRange.BIAS_AFTER;

export class DateRange {
  static readonly BIAS_BEFORE = 'before';
  static readonly BIAS_AFTER = 'after';
  length: number;

  constructor(public readonly from: LocalDate, public readonly to: LocalDate) {
    this.length = Math.abs(from.date.diff(to.date, 'days'));
  }

  static fromPrimitives(primitives: { from: string; to: string }) {
    return new DateRange(LocalDate.fromString(primitives.from), LocalDate.fromString(primitives.to));
  }

  isInRange(date: LocalDate): boolean {
    return date.atom >= this.from.atom && date.atom <= this.to.atom;
  }

  intersects(other: DateRange): boolean {
    // Use .ymd instead of .atom since it should not include the hour.
    return this.from.ymd <= other.to.ymd && this.to.ymd >= other.from.ymd;
  }

  intersection(other: DateRange): DateRange | null {
    if (!this.intersects(other)) {
      return null;
    }

    const from = this.from.atom > other.from.atom ? this.from : other.from;
    const to = this.to.atom < other.to.atom ? this.to : other.to;

    return new DateRange(from, to);
  }

  static oneMonthBeforeAndAfter(): DateRange {
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.subtract(1, 'month').startOf('month').format()),
      LocalDate.fromIsoString(LocalDate.now().date.add(1, 'month').endOf('month').format())
    );
  }

  static aroundDay(totalDays: number, day: string, bias: DateRangeBias = DateRange.BIAS_AFTER): DateRange {
    const half = Math.floor(totalDays / 2);
    const isEven = totalDays % 2 === 0;

    const daysBefore = isEven ? (bias === 'before' ? half : half - 1) : half;
    const daysAfter = isEven ? (bias === 'after' ? half : half - 1) : half;

    const center = LocalDate.fromString(day).date;
    const from = center.clone().subtract(daysBefore, 'days');
    const to = center.clone().add(daysAfter, 'days');

    return new DateRange(LocalDate.fromIsoString(from.format()), LocalDate.fromIsoString(to.format()));
  }

  static aroundToday(totalDays: number): DateRange {
    return DateRange.aroundDay(totalDays, LocalDate.now().ymd);
  }

  static thisWeekRange(): DateRange {
    moment.locale('es');
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.startOf('week').format()),
      LocalDate.fromIsoString(LocalDate.now().date.endOf('week').format())
    );
  }

  static thisMonthRange(): DateRange {
    moment.locale('es');
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.startOf('month').format()),
      LocalDate.fromIsoString(LocalDate.now().date.endOf('month').format())
    );
  }

  static lastMonthRange(): DateRange {
    moment.locale('es');
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.subtract(1, 'month').startOf('month').format()),
      LocalDate.fromIsoString(LocalDate.now().date.subtract(1, 'month').endOf('month').format())
    );
  }

  static sevenDaysBeforeAndAfterRange(): DateRange {
    moment.locale('es');
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.subtract(7, 'days').format()),
      LocalDate.fromIsoString(LocalDate.now().date.add(7, 'days').format())
    );
  }

  static threeMonthsBeforeAndAfterRange(): DateRange {
    moment.locale('es');
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.startOf('month').subtract(3, 'month').startOf('month').format()),
      LocalDate.fromIsoString(LocalDate.now().date.startOf('month').add(3, 'month').endOf('month').format())
    );
  }

  static today(): DateRange {
    return new DateRange(
      LocalDate.fromIsoString(LocalDate.now().date.startOf('day').format()),
      LocalDate.fromIsoString(LocalDate.now().date.endOf('day').format())
    );
  }

  fulfillsMaxDaysRange(maxDaysRange: number): boolean {
    return this.length <= maxDaysRange;
  }

  toYMDObject(): { from: string; to: string } {
    return { from: this.from.ymd, to: this.to.ymd };
  }
}
